import { useEffect, useState } from 'react';
import type { DistrictMapPmtiles } from '@citizen-walk/shared-types';
import { getOfflineMapEntry } from './offlineMap';
import { useDistrictPmtiles } from './useDistrictPmtiles';

/**
 * `district.mapDownloadPolicy === 'auto'` の地区で、まだ pmtiles が端末に保存されていないとき、
 * 初回 mount で自動 prompt を出すための判定 hook (旧 `old/apps/user/src/components/AutoMapDialog.tsx`
 * useEffect L37-60 踏襲、Sprint BG-DataSettings 19、U-C13)。
 *
 * 表示条件 (旧踏襲):
 *  - district.mapPmtiles が存在
 *  - district.mapDownloadPolicy === 'auto'
 *  - 端末にまだ該当地区の pmtiles が無い、または storagePath 不一致 (旧版を持っている)
 *  - 過去に「今後表示しない」(localStorage 永続化) を選んでいない
 *
 * 旧との差分:
 *  - 旧 localStorage key: `auto-map-dismissed:${districtId}`
 *    v2 では命名規約に揃え `cw.autoMap.prompted.{districtId}` を採用 (CitizenWalk v2 の
 *    localStorage 命名 prefix `cw.` 一致)。旧キーは migration なし (本機能は v2 まで未実装だったため
 *    既存 user で旧キーを保持しているケースは無い)。
 *  - 旧は district オブジェクトを直接受領していたが、v2 では useDistrictPmtiles から meta+policy
 *    を引く形に分離 (`ResolveDistrictResult` に mapPmtiles/mapDownloadPolicy を含めない v2 設計のため)。
 *
 * 重複防止:
 *  - dismissForever() を呼ぶと `localStorage[cw.autoMap.prompted.{districtId}] = '1'`。
 *  - 一度 dismiss すると同 district で再度 hook が呼ばれても visible=false で確定。
 *  - dismiss flag は意図的に手動 reset (UI 削除 / localStorage.clear()) 以外解除されない。
 */

export const dismissedKey = (districtId: string): string => `cw.autoMap.prompted.${districtId}`;

export interface UseAutoMapPromptResult {
  /** dialog を表示すべきか (条件全て満たす + 未 dismiss + cache miss)。 */
  visible: boolean;
  /** 対象 pmtiles メタデータ (visible=true のときのみ non-null)。 */
  meta: DistrictMapPmtiles | null;
  /** 一時 close (今回限り、再起動で再表示)。 */
  close: () => void;
  /** 永続 dismiss (localStorage 書込、以後表示しない)。 */
  dismissForever: () => void;
}

export function useAutoMapPrompt(districtId: string | null): UseAutoMapPromptResult {
  const pmtilesState = useDistrictPmtiles(districtId);
  const [closed, setClosed] = useState(false);
  // cache lookup 結果。null=未確認 / undefined=cache miss / OfflineMapEntry=cache hit
  const [cacheChecked, setCacheChecked] = useState<{ storagePath: string | null } | null>(null);

  useEffect(() => {
    // 条件未満時は cache check 不要
    if (!districtId) return;
    if (pmtilesState.status !== 'ready') return;
    if (pmtilesState.policy !== 'auto') return;

    // 永続 dismiss 済 → 即決で表示しない
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(dismissedKey(districtId)) === '1';
    } catch {
      /* localStorage 不可 (private mode 等) は表示扱い */
    }
    if (dismissed) return;

    let cancelled = false;
    void getOfflineMapEntry(districtId).then((entry) => {
      if (cancelled) return;
      setCacheChecked({ storagePath: entry?.storagePath ?? null });
    });
    return () => {
      cancelled = true;
    };
    // policy / status / storagePath が変わったら再評価
  }, [
    districtId,
    pmtilesState.status,
    pmtilesState.status === 'ready' ? pmtilesState.policy : null,
    pmtilesState.status === 'ready' ? pmtilesState.meta.storagePath : null,
  ]);

  // visible 計算
  let visible = false;
  let meta: DistrictMapPmtiles | null = null;
  if (
    !closed &&
    districtId &&
    pmtilesState.status === 'ready' &&
    pmtilesState.policy === 'auto' &&
    cacheChecked !== null
  ) {
    // dismiss 済再確認 (state 反映のタイムラグ対策、render 中も synchronous に確認)
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(dismissedKey(districtId)) === '1';
    } catch {
      /* ignore */
    }
    if (!dismissed) {
      // cache miss / version mismatch
      if (cacheChecked.storagePath !== pmtilesState.meta.storagePath) {
        visible = true;
        meta = pmtilesState.meta;
      }
    }
  }

  return {
    visible,
    meta,
    close: () => setClosed(true),
    dismissForever: () => {
      if (!districtId) return;
      try {
        localStorage.setItem(dismissedKey(districtId), '1');
      } catch {
        /* localStorage 不可 (private mode / quota) は session 内 only close */
      }
      setClosed(true);
    },
  };
}
